import { useEffect, useState } from "react";

import { Callout, Classes, Icon } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import {
  ComponentTypeEnum,
  GetProgramCoachingSessionsQuery,
  useGetProgramCoachingSessionParticipantsLazyQuery,
} from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

import { OneOneMeetingDateTimeDisplay } from "./program-component/panels/OneOneMeetingDateTimeDisplay";
import { ParticipantResponseStatusTableV2 } from "./program-details/ParticipantResponseStatusTableV2";
import { Participant } from "./program-participants/participant-table-utils";
import { ONE_ONE_COACHING_STAGE_STYLES } from "./utils";

type Session = NonNullableArrayItem<
  GetProgramCoachingSessionsQuery["programCoachingSessions"]
>;

export const getStatisticDataFormatedForItem = ({
  session,
}: {
  session: Session;
}) => {
  const status = session.componentStatus;
  const totalParticipantNumber = session.totalParticipantsCount || 0;
  const scheduledCount = session.scheduledCount || 0;
  const completedCount = session.completedCount || 0;
  let incompleteCount =
    totalParticipantNumber - completedCount - scheduledCount;

  if (incompleteCount < 0) {
    incompleteCount = 0;
  }

  const statisticData: {
    label: string;
    color: string;
    borderColor: string;
    count: number;
  }[] = [
    {
      label: ONE_ONE_COACHING_STAGE_STYLES["Completed"].text,
      color: ONE_ONE_COACHING_STAGE_STYLES["Completed"].color,
      borderColor: ONE_ONE_COACHING_STAGE_STYLES["Completed"].borderColor,
      count: completedCount,
    },
    {
      label: ONE_ONE_COACHING_STAGE_STYLES["Scheduled"].text,
      color: ONE_ONE_COACHING_STAGE_STYLES["Scheduled"].color,
      borderColor: ONE_ONE_COACHING_STAGE_STYLES["Scheduled"].borderColor,
      count: scheduledCount,
    },
  ];

  if (status === "Completed") {
    statisticData.push({
      label: ONE_ONE_COACHING_STAGE_STYLES["Missed"].text,
      color: ONE_ONE_COACHING_STAGE_STYLES["Missed"].color,
      borderColor: ONE_ONE_COACHING_STAGE_STYLES["Missed"].borderColor,
      count: incompleteCount || 0,
    });
  } else if (status && ["Ongoing", "Upcoming"].includes(status)) {
    statisticData.push({
      label: ONE_ONE_COACHING_STAGE_STYLES["NotStarted"].text,
      color: ONE_ONE_COACHING_STAGE_STYLES["NotStarted"].color,
      borderColor: ONE_ONE_COACHING_STAGE_STYLES["NotStarted"].borderColor,
      count: incompleteCount || 0,
    });
  }
  return statisticData;
};

export function MeetingStatistic({ session }: { session: Session }) {
  const { id } = session;
  const [showStatusTable, setShowStatusTable] = useState(false);

  const statisticData = getStatisticDataFormatedForItem({
    session,
  });

  const isIntroSession = session.type === ComponentTypeEnum.OneOneCoachingIntro;

  const [fetch, { data, loading }] =
    useGetProgramCoachingSessionParticipantsLazyQuery({
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (showStatusTable) {
      fetch({ variables: { childProgramComponentId: id } });
    }
  }, [showStatusTable]);

  const participants = ensureNonNullArray(
    data?.programCoachingSessionParticipants,
  );

  return (
    <Box key={id} mt={24} className={CARD_CLASSES} p={16}>
      <Box fontWeight={500} color={SUBTEXT_COLOR} fontSize={16} mb={2}>
        {isIntroSession ? "Find My Coach" : session.name}
      </Box>
      <OneOneMeetingDateTimeDisplay meeting={session} />
      <>
        {/* chart */}
        <Box my={16} flex={1} height={12} width={1} display={["none", "flex"]}>
          {statisticData
            .filter((item) => item.count > 0)
            .map((item, index) => (
              <Flex
                key={item.label}
                flex={item.count}
                justifyContent={"center"}
                css={
                  index !== statisticData.length - 1 ? { marginRight: 5 } : null
                }
              >
                <Box
                  width={1}
                  backgroundColor={item.color}
                  height={12}
                  css={{
                    border: `solid 1px ${item.borderColor}`,
                    borderRadius: 4,
                  }}
                ></Box>
              </Flex>
            ))}
        </Box>

        {/* stats */}
        <Flex flex={1} flexDirection={["column", "row"]} style={{ gap: 24 }}>
          {statisticData.map((item) => (
            <Box key={item.label} justifyContent={"center"}>
              <Box color={SUBTEXT_COLOR}>{item.label}</Box>
              <Flex alignItems={"center"}>
                <Box
                  backgroundColor={item.color}
                  width={12}
                  height={12}
                  css={{
                    border: `solid 1px ${item.borderColor}`,
                    borderRadius: 4,
                  }}
                ></Box>
                <Box fontSize={18} fontWeight={600} ml={"5px"}>
                  {item.count}
                </Box>
              </Flex>
            </Box>
          ))}
        </Flex>
        <Box my={3}>
          <a onClick={() => setShowStatusTable(!showStatusTable)}>
            See Progress Per Participant{" "}
            <Icon icon={!showStatusTable ? "chevron-down" : "chevron-up"} />
          </a>
        </Box>
        {showStatusTable &&
          (loading ? (
            <Box mt={4} className={Classes.SKELETON} height={100} />
          ) : participants.length > 0 ? (
            <Box>
              <ParticipantResponseStatusTableV2
                participants={participants as Participant[]}
                component={session}
              />
            </Box>
          ) : (
            <Box mt={4}>
              <Callout intent="primary" icon={null}>
                No responses found
              </Callout>
            </Box>
          ))}
      </>
    </Box>
  );
}
